
	import { defineComponent, PropType, ref } from 'vue';
	import { useRoute } from 'vue-router';
	import { IToaXeResponseBase } from '@/core/interfaces/ApiResponses';
	import { useRouterCustom } from '@/core/hooks/use-router-custom';
	import { withListPage } from '@/core/helpers/list-page-context';
	import { getErrorMsg, swalNotification } from '@/core/helpers/utils';
	import { usePagination } from '@/core/hooks/use-pagination';
	import Setting from '@/components/shared/setting/Setting.vue';
	import type { SettingAction } from '@/components/shared/setting/Setting.vue';
	import ToaXeService from '@/core/services/ToaXe.service';
	import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog.vue';
	import ToaXeDetailModal from '@/components/toa-xe/ToaXeDetailModal.vue';
	import BaseLink from '@/components/shared/base-link/BaseLink.vue';
	import IconEye from '@/icons/IconEye.vue';
	import IconPencil from '@/icons/IconPencil.vue';
	import IconTrash from '@/icons/IconTrash.vue';

	export default defineComponent({
		name: 'danh-sach-toa-xe-table',

		components: {
			BaseLink,
			ConfirmationDialog,
			Setting,
			ToaXeDetailModal,
		},

		emits: ['delete-toa-xe-success', 'on-delete-button-clicked'],

		setup() {
			const { push } = useRouterCustom();
			const route = useRoute();

			const { indexCal } = usePagination();

			const selectedToaXeId = ref<number | undefined>(undefined);

			return {
				indexCal,
				push,
				route,
				selectedToaXeId,
			};
		},

		props: {
			curPage: {
				type: Number,
				default: 0,
			},
			deleteExternally: {
				type: Boolean as PropType<boolean>,
				default: false,
			},
			items: {
				type: Array as PropType<Array<IToaXeResponseBase>>,
			},
			isLoading: {
				type: Boolean,
				default: false,
			},
			perPage: {
				type: Number,
				default: 0,
			},
			showDmToaXe: {
				type: Boolean,
				default: false,
			},
			showDeleteToaXeButtonInModal: {
				type: Boolean as PropType<boolean>,
				required: true,
			},
			showDeleteToaXeButtonInTable: {
				type: Boolean as PropType<boolean>,
				required: true,
			},
			showMaToaXe: {
				type: Boolean,
				default: false,
			},
			showChungLoai: {
				type: Boolean,
				default: false,
			},
			/** When set (update page), highlight the row for the item being edited */
			selectedId: {
				type: Number,
				default: 0,
			},
		},

		data: () => ({
			deletingToaXe: undefined as IToaXeResponseBase | undefined,
			isDeletingModalDisplay: false,
		}),

		methods: {
			rowClassName({ row }: { row: IToaXeResponseBase }): string {
				if (this.selectedId > 0 && row.id === this.selectedId) {
					return 'current-row';
				}

				return '';
			},

			getSettingActions(row: IToaXeResponseBase): SettingAction[] {
				return [
					{
						icon: IconEye,
						label: 'Chi tiết',
						tooltip: 'Chi tiết toa xe',
						onClick: () => {
							this.selectedToaXeId = row.id;
						},
					},
					{
						icon: IconPencil,
						label: 'Chỉnh sửa',
						tooltip: 'Chỉnh sửa toa xe',
						any: ['master:toaxe:sua'],
						onClick: () => {
							const path = `/quan-ly/toa-xe/${row.id}/chinh-sua`;
							const currentId = Number(this.route.params.id);

							// Same edit page — push is a no-op; scroll to form instead
							if (
								this.route.name === 'toa-xe-cap-nhat' &&
								currentId === row.id
							) {
								window.scrollTo({ top: 0, behavior: 'smooth' });

								return;
							}

							void this.push(withListPage(path, this.curPage));
						},
					},
					{
						icon: IconTrash,
						label: 'Xóa',
						any: ['master:toaxe:xoa'],
						isDisabled: !this.canDeleteToaXe(row),
						tooltip: this.canDeleteToaXe(row) ? '' : 'Không thể xóa toa xe này',
						onClick: () => this.onDeleteButtonClicked(row.id),
					},
				];
			},

			canDeleteToaXe(toaXe?: IToaXeResponseBase) {
				if (!toaXe) return false;

				if (!this.showDeleteToaXeButtonInTable) return false;

				if (!this.deleteExternally) return toaXe.can_delete;

				return true;
			},
			onDeleteButtonClicked(id) {
				const toaXe = this.items?.find((item) => item.id === id);

				if (this.deleteExternally) {
					this.$emit('on-delete-button-clicked', {
						toaXe,
					});

					return;
				}

				this.isDeletingModalDisplay = true;

				if (toaXe) {
					this.deletingToaXe = toaXe;
				}
			},

			async confirmDelete() {
				this.isDeletingModalDisplay = false;

				try {
					if (this.deletingToaXe)
						await ToaXeService.delete(this.deletingToaXe?.id);
					await swalNotification('Xóa thành công', 'success');

					this.$emit('delete-toa-xe-success');
				} catch (error) {
					await swalNotification(
						getErrorMsg(error, 'Có lỗi xảy ra, không thể xóa toa xe này'),
						'error',
					);
				}
			},
		},
	});
